import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { dateChip } from "@/lib/utils";
import type { Meeting } from "@/types";

export default function HomePage() {
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Meeting | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    setLoading(true);
    try {
      const data = await api.listMeetings();
      setMeetings(data.meetings);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete() {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await api.deleteMeeting(confirmDelete.id);
      setMeetings((prev) => prev.filter((m) => m.id !== confirmDelete.id));
      setConfirmDelete(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="max-w-5xl mx-auto px-6 py-12">
      <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
        <div className="min-w-0 max-w-full">
          <h1 className="font-serif text-3xl font-bold text-brand-dark mb-2">
            會議紀錄
          </h1>
          <p className="text-sm text-brand-dark/60">
            把會議資料整理成可以給業主看的網頁紀錄
          </p>
        </div>
        <Link
          to="/new"
          className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-brand-dark text-brand-cream rounded-full text-sm font-medium hover:bg-brand-gold transition-colors"
        >
          <svg
            className="w-4 h-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
          >
            <path d="M12 5v14M5 12h14" />
          </svg>
          <span>新增會議</span>
        </Link>
      </div>

      {loading ? (
        <div className="text-center py-20 text-brand-dark/40">載入中⋯⋯</div>
      ) : error ? (
        <div className="text-center py-20 text-red-700">
          載入失敗：{error}
        </div>
      ) : meetings.length === 0 ? (
        <div className="text-center py-20 text-brand-dark/40">
          <p>還沒有任何會議紀錄</p>
          <p className="text-xs mt-2">點上方「新增會議」開始</p>
        </div>
      ) : (
        <ul className="space-y-3">
          {meetings.map((m) => (
            <li key={m.id}>
              <MeetingRow
                meeting={m}
                onAskDelete={() => setConfirmDelete(m)}
              />
            </li>
          ))}
        </ul>
      )}

      {confirmDelete && (
        <ConfirmDeleteModal
          meeting={confirmDelete}
          loading={deleting}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={handleDelete}
        />
      )}
    </div>
  );
}

function MeetingRow({
  meeting,
  onAskDelete,
}: {
  meeting: Meeting;
  onAskDelete: () => void;
}) {
  const chip = dateChip(meeting.meeting_date, meeting.created_at);

  return (
    <div className="group relative flex items-stretch border border-brand-gray/20 rounded-lg bg-white hover:border-brand-gold/60 hover:shadow-sm transition-all overflow-hidden">
      {/* 日期 chip */}
      <Link
        to={`/meeting/${meeting.id}`}
        className="flex flex-col items-center justify-center px-5 py-4 bg-brand-cream/60 border-r border-brand-gray/15 min-w-[88px] num"
      >
        <div className="text-[10px] uppercase tracking-[0.2em] text-brand-gold font-medium">
          {chip.month}月
        </div>
        <div className="text-3xl font-bold text-brand-dark leading-none mt-1">
          {chip.day}
        </div>
        <div className="text-[10px] text-brand-dark/50 mt-1.5">
          {chip.year}
        </div>
      </Link>

      {/* 標題 + 業主 */}
      <Link
        to={`/meeting/${meeting.id}`}
        className="flex-1 px-5 py-4 flex items-center min-w-0"
      >
        <div className="min-w-0 w-full">
          <div className="font-medium text-brand-dark truncate">
            {meeting.title}
          </div>
          <div className="text-xs text-brand-dark/50 mt-1.5 flex items-center gap-2 flex-wrap">
            {meeting.client_name ? (
              <span className="nb">{meeting.client_name}</span>
            ) : (
              <span className="italic text-brand-dark/30">未填業主</span>
            )}
            <span className="text-brand-gray">·</span>
            <SourceLabel source={meeting.source_type} />
          </div>
        </div>
      </Link>

      {/* 狀態 + 刪除 */}
      <div className="flex items-center gap-2 px-4 py-4">
        <StatusBadge status={meeting.status} />
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onAskDelete();
          }}
          aria-label="刪除"
          title="刪除"
          className="p-1.5 rounded-md text-brand-dark/30 hover:text-red-700 hover:bg-red-50 transition-colors opacity-60 group-hover:opacity-100"
        >
          <svg
            className="w-4 h-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6M10 11v6M14 11v6" />
          </svg>
        </button>
      </div>
    </div>
  );
}

function SourceLabel({ source }: { source: Meeting["source_type"] }) {
  const map: Record<Meeting["source_type"], string> = {
    plaud: "PLAUD 連結",
    audio: "音檔",
    manual: "手動筆記",
    mixed: "多來源",
  };
  return (
    <span className="text-[11px] text-brand-dark/45">
      {map[source] ?? source}
    </span>
  );
}

function StatusBadge({ status }: { status: Meeting["status"] }) {
  const map = {
    draft: { label: "草稿", cls: "bg-brand-gray/15 text-brand-dark/50" },
    queued: { label: "排隊中", cls: "bg-blue-50 text-blue-700" },
    processing: {
      label: "處理中",
      cls: "bg-brand-gold/15 text-brand-gold",
    },
    awaiting_cli: {
      label: "等 Claude Code",
      cls: "bg-brand-gold/10 text-brand-dark/70",
    },
    completed: { label: "完成", cls: "bg-brand-green/15 text-brand-green" },
    failed: { label: "失敗", cls: "bg-red-50 text-red-700" },
    archived: { label: "封存", cls: "bg-brand-gray/15 text-brand-gray" },
  } as const;
  const v = map[status] ?? map.draft;
  return (
    <span
      className={`text-[11px] px-2 py-0.5 rounded-full whitespace-nowrap ${v.cls}`}
    >
      {v.label}
    </span>
  );
}

function ConfirmDeleteModal({
  meeting,
  loading,
  onCancel,
  onConfirm,
}: {
  meeting: Meeting;
  loading: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="font-serif text-lg font-bold text-brand-dark mb-2">
          要刪除這場會議嗎？
        </h2>
        <p className="text-sm text-brand-dark/60 leading-relaxed">
          <span className="nb font-medium text-brand-dark">{meeting.title}</span>
          {meeting.client_name && (
            <>
              <span className="mx-1 text-brand-gray">·</span>
              <span className="nb">{meeting.client_name}</span>
            </>
          )}
          <br />
          會議內容、業主資料、對話歷史都會一併移除，無法復原。
        </p>
        <div className="mt-6 flex justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-2 text-sm text-brand-dark/70 hover:text-brand-dark"
          >
            取消
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="px-4 py-2 text-sm bg-red-700 text-white rounded-md hover:bg-red-800 disabled:opacity-60"
          >
            {loading ? "刪除中⋯⋯" : "確定刪除"}
          </button>
        </div>
      </div>
    </div>
  );
}
